import { useMemo } from 'react';
import type { ContestType } from '@betterpool/api-types/contests-service';
import { useRouter } from 'next/router';
import { match } from 'ts-pattern';

import { URLQueryKeys } from '~/domains/common/types/URLQueryKeys';
import { useIsNotebook } from '~/domains/common/hooks/mediaQueries';
import type { GetMyContestsV2Params } from '~/services/contests/contests-v2.service.api';

import { ContestStatusFilter } from '../../services/enums/contest-status-filter.enum';
import parseMyContestsV2 from '../utils/parseMyContestsV2';
import isNetworkError from '../utils/isNetworkError';

import useEntriesUserV2 from './useEntriesUserV2';

type MyContestsSortOption = NonNullable<GetMyContestsV2Params['sortOption']>;

const SORT_OPTION_TO_V2_SORT_OPTION: Record<string, MyContestsSortOption> = {
  SLATE_START: 'slate_start',
  END_DATE: 'end_date',
  ENTERED_DATE: 'entered_date',
  ENTRY_FEE: 'entry_fee',
  PRIZE_POOL: 'prize_pool',
};

const V2_SORT_OPTIONS = new Set(Object.values(SORT_OPTION_TO_V2_SORT_OPTION));

const getQueryStringValue = (value?: string | string[]) =>
  Array.isArray(value) ? value[0] : value;

const getSortOption = (value?: string | string[]): GetMyContestsV2Params['sortOption'] => {
  const sortOption = getQueryStringValue(value);

  if (!sortOption) return undefined;
  if (SORT_OPTION_TO_V2_SORT_OPTION[sortOption]) return SORT_OPTION_TO_V2_SORT_OPTION[sortOption];
  if (V2_SORT_OPTIONS.has(sortOption as MyContestsSortOption)) {
    return sortOption as MyContestsSortOption;
  }

  return undefined;
};

const getSortDir = (value?: string | string[]): GetMyContestsV2Params['sortDir'] => {
  const normalizedValue = getQueryStringValue(value)?.toLowerCase();

  if (normalizedValue === 'asc' || normalizedValue === 'desc') return normalizedValue;
  return undefined;
};

const useMyContests = ({
  status,
  limit,
  enabled,
  keepEliminatedActive,
  hideEliminated,
}: {
  status?: ContestStatusFilter;
  limit?: number;
  enabled?: boolean;
  keepEliminatedActive?: boolean;
  hideEliminated?: boolean;
} = {}) => {
  const router = useRouter();
  const currentQuery = { ...router.query };

  const URLContestStatusFilter =
    (currentQuery[URLQueryKeys.CONTEST_STATUS] as ContestStatusFilter) ||
    status ||
    ContestStatusFilter.ACTIVE;
  const URLLeagueFilter = currentQuery[URLQueryKeys.LEAGUES] as string;
  const URLContestTypeFilter = currentQuery[URLQueryKeys.CONTEST_TYPE] as ContestType;
  const URLSortOption = getSortOption(currentQuery[URLQueryKeys.SORT_BY]);
  const URLSortDir = getSortDir(currentQuery[URLQueryKeys.SORT_ORDER]);
  const URLHasEntryFee = useMemo(() => {
    const queryValue = currentQuery[URLQueryKeys.HAS_ENTRY_FEE];

    if (!queryValue) {
      return undefined;
    }

    return queryValue === 'true';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(currentQuery)]);
  const isNotebook = useIsNotebook();
  const dataLimit = limit ?? (isNotebook ? 20 : 21);

  const data = useEntriesUserV2({
    status: URLContestStatusFilter,
    contestType: URLContestTypeFilter,
    leagues: URLLeagueFilter ? [URLLeagueFilter] : undefined,
    limit: dataLimit,
    hasEntryFee: URLHasEntryFee,
    keepEliminatedActive,
    sortOption: URLSortOption,
    sortDir: URLSortDir,
    enabled,
  });

  const flatUserContests = useMemo(() => {
    const statusFiltered =
      match(URLContestStatusFilter)
        .with(ContestStatusFilter.SCHEDULED, () =>
          data.items?.filter((item) => !item.contest.isStarted)
        )
        .with(ContestStatusFilter.IN_PROGRESS, () =>
          data.items?.filter((item) => item.contest.isStarted)
        )
        .otherwise(() => data.items) ?? [];

    if (!hideEliminated) {
      return statusFiltered;
    }

    return statusFiltered.filter((item) => item.leaderboard?.isUserEliminated !== true);
  }, [URLContestStatusFilter, data.items, hideEliminated]);

  const parsedContests = useMemo(() => parseMyContestsV2(flatUserContests), [flatUserContests]);
  const hasCachedContests = !!data.data;
  const isError = data.isError && !hasCachedContests;

  return {
    contests: parsedContests,
    isLoading: data.isLoading,
    isError,
    isConnectionError: isError && isNetworkError(data.error),
    refetch: data.refetch,
    fetchNextPage: data.fetchNextPage,
    hasNextPage: data.hasNextPage && !data.isFetchNextPageError,
    itemsTotal: data.itemsTotal,
    contestStatusFilter: URLContestStatusFilter,
  };
};

export default useMyContests;
